/**
 * File Utilities
 * 
 * Functions for file type detection and file information
 */

// Supported document MIME types
const DOCUMENT_MIME_TYPES = [
  "application/pdf",
  "application/msword",
  "application/vnd.openxmlformats-officedocument.wordprocessingml.document",
  "application/vnd.ms-excel",
  "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
  "application/vnd.ms-powerpoint",
  "application/vnd.openxmlformats-officedocument.presentationml.presentation",
  "text/plain",
  "application/rtf",
];

// Supported document file extensions
const DOCUMENT_EXTENSIONS = [
  ".pdf", ".doc", ".docx", ".xls", ".xlsx", ".ppt", ".pptx", ".txt", ".rtf"
];

/**
 * Check if a media object is a document file
 * @param {Object} media - Media object
 * @returns {boolean} True if document file
 */
export function isDocumentFile(media) {
  if (!media) return false;

  // Check by mediaType and mimeType (from API response)
  if (media.mediaType === "FILE" && media.file?.mimeType) {
    return DOCUMENT_MIME_TYPES.includes(media.file.mimeType);
  }

  // Fallback: check by file extension
  const url = media.mediaUrl || media.file?.url || "";
  const lowerUrl = url.toLowerCase();
  return DOCUMENT_EXTENSIONS.some(ext => lowerUrl.endsWith(ext));
}

/**
 * Check if a media object is a PDF file
 * @param {Object} media - Media object
 * @returns {boolean} True if PDF file
 */
export function isPdfFile(media) {
  if (!media) return false;

  // Check by mediaType and mimeType (from API response)
  if (
    media.mediaType === "FILE" &&
    media.file?.mimeType === "application/pdf"
  ) {
    return true;
  }

  // Fallback: check by file extension
  const url = media.mediaUrl || media.file?.url || "";
  return url.toLowerCase().endsWith(".pdf");
}

/**
 * Format file size in human-readable format
 * @param {number} bytes - File size in bytes
 * @returns {string} Formatted file size (e.g., "1.5 MB")
 */
export function formatFileSize(bytes) {
  if (!bytes || bytes === 0) return "0 KB";

  const sizeInBytes = parseInt(bytes);
  const KB = 1024;
  const MB = KB * 1024;
  const GB = MB * 1024;

  if (sizeInBytes >= GB) {
    return `${(sizeInBytes / GB).toFixed(2)} GB`;
  } else if (sizeInBytes >= MB) {
    return `${(sizeInBytes / MB).toFixed(2)} MB`;
  } else if (sizeInBytes >= KB) {
    return `${(sizeInBytes / KB).toFixed(2)} KB`;
  } else {
    return `${sizeInBytes} B`;
  }
}

/**
 * Get file name from media object
 * @param {Object} media - Media object
 * @returns {string} File name
 */
export function getFileName(media) {
  if (!media) return "Document";

  // Try to get original name from file object
  if (media.file?.originalName) {
    return media.file.originalName;
  }

  // Fallback: extract from URL
  const url = media.mediaUrl || media.file?.url || "";
  const filename = url.split("/").pop();

  // Decode URI component to handle special characters
  try {
    return decodeURIComponent(filename);
  } catch (e) {
    return filename;
  }
}

